import { useCallback, useState } from "react";
import { showTestJobsStore } from "@gui/infrastructure/pass-through/storage";
import { useConfirm } from "@gui/contexts/confirm";
import type { ConfirmOptions } from "@gui/widgets/ConfirmDialog";

export interface ShowTestJobsToggleViewModel {
	showTestJobs: boolean;
	toggle: () => Promise<void>;
}

/** The browser effect the toggle needs; a parameter only so tests can observe it. */
export interface ShowTestJobsBrowser {
	reload: () => void;
}

const realBrowser: ShowTestJobsBrowser = {
	reload: () => window.location.reload(),
};

const RELOAD_WARNING = "The page will reload, so any unsaved changes on it will be lost.";
export const SHOW_CONFIRM: ConfirmOptions = {
	title: "Show test jobs?",
	body: RELOAD_WARNING,
	confirmLabel: "Show Test Jobs",
};
export const HIDE_CONFIRM: ConfirmOptions = {
	title: "Hide test jobs?",
	body: RELOAD_WARNING,
	confirmLabel: "Hide Test Jobs",
};

/**
 * Flips the persisted "Show Test Jobs" switch, then reloads the page.
 *
 * The switch changes what the API returns (see `showTestJobsStore`), and every list already on
 * screen — the issues queues, their counts, the scheduling backlog — was fetched under the old
 * setting. A reload is the one refresh that cannot miss a cache, the same reasoning as the hard
 * navigation on logout.
 *
 * The reload is also why it asks first: the item can be clicked from any page, and whatever that
 * page holds unsaved (a scheduler's pending shifts, a half-edited job) goes with it. The toggle
 * cannot know what each page is holding, so it always confirms rather than guess.
 */
export function useShowTestJobsToggleViewModel(
	browser: ShowTestJobsBrowser = realBrowser,
): ShowTestJobsToggleViewModel {
	const [showTestJobs, setShowTestJobs] = useState(() => showTestJobsStore.get());
	const confirm = useConfirm();

	const toggle = useCallback(async () => {
		const next = !showTestJobs;
		if (!(await confirm(next ? SHOW_CONFIRM : HIDE_CONFIRM))) return;
		showTestJobsStore.set(next);
		setShowTestJobs(next);
		browser.reload();
	}, [showTestJobs, browser, confirm]);

	return { showTestJobs, toggle };
}
